import { onlineManager } from "@tanstack/react-query";
import { call, put, takeEvery, takeLeading, fork } from "redux-saga/effects";
import FullStory from "services/Fullstory";
import { API_CLIENT_ACTIONS } from "services/ApiClient";

import { AuthReset } from "core/Auth";
import QueryString from "core/QueryString";
import ErrorLoggerService from "core/ErrorLoggerService";

import { replace } from "../utils/navigation";

import { appActions } from "../containers/App/App.slice";
import { toggleRequestsInProgressStatus } from "../containers/Shared/Shared.slice";
import { showConfirmDialogAndWaitForAction } from "../containers/Confirm/Confirm.saga";

import routesObject from "../routesObject";

// --- Types ---

interface RequestFailedPayload {
  url?: string;
  method?: string;
  status?: number;
  code?: string;
  message?: string;
}

interface ApiErrorItem {
  code?: { name?: string };
  title?: string;
  message?: string;
}

interface ResponseReceivedPayload {
  config: {
    url?: string;
  };
  status: number;
  actualResponseData?: {
    errors?: ApiErrorItem[] | null;
  };
  data?: {
    errors?: ApiErrorItem[] | null;
  };
}

function* handleRequestProgressBar() {
  let reqCount = 0;
  let resCount = 0;
  yield takeEvery(API_CLIENT_ACTIONS.REQUEST_SENT, function* () {
    reqCount++;
    if (reqCount !== resCount) {
      yield put(toggleRequestsInProgressStatus(true));
    }
  });
  yield takeEvery(
    [ API_CLIENT_ACTIONS.RESPONSE_RECEIVED, API_CLIENT_ACTIONS.REQUEST_FAILED ],
    function* () {
      resCount++;
      if (reqCount === resCount) {
        yield put(toggleRequestsInProgressStatus(false));
      }
    }
  );
}

function* handleResponseErrors(
  statusHandlingInFlight: Set<number>,
  { payload }: { payload: ResponseReceivedPayload }
) {
  try {
    const { config, status, actualResponseData, data } = payload;

    const errors = actualResponseData?.errors ?? data?.errors;
    const api = config?.url;
    const firstError = errors?.[0];
    const code = firstError?.code?.name;
    const title = firstError?.title;
    const message = firstError?.message;

    if (status === 422 && errors?.length) {
      errors.forEach((err) => {
        FullStory.sendEvent("Business Exception", {
          api,
          code: err.code?.name,
          title: err.title,
          message: err.message
        });
      });
    }

    if (statusHandlingInFlight.has(status)) return;
    statusHandlingInFlight.add(status);

    try {
      switch (status) {
        case 401:
          yield call(AuthReset);
          yield call(replace, {
            pathname: routesObject.login.url,
            search: QueryString.stringify({
              redirectTo: encodeURIComponent(
                window.location.pathname + window.location.search
              )
            })
          });
          yield put(appActions.authUserLogout());
          FullStory.sendEvent("Unauthorized", {
            api,
            code,
            title,
            message
          });
          yield call(showConfirmDialogAndWaitForAction, {
            id: "error-401-dialog",
            icon: "info",
            title: "Su sesión ha caducado.",
            content: "Por favor ingrese nuevamente."
          });
          break;

        case 404:
          FullStory.sendEvent("Not Found", {
            api,
            code,
            title,
            message
          });
          yield call(showConfirmDialogAndWaitForAction, {
            id: "error-404-dialog",
            subType: "warning",
            title: "404",
            content:
              "No conseguimos encontrar lo que estás buscando. Ir a la página de inicio."
          });
          yield call(replace, routesObject.home.url);
          break;

        case 429:
          FullStory.sendEvent("Too Many Requests", {
            api,
            code,
            title,
            message
          });
          yield fork(showConfirmDialogAndWaitForAction, {
            id: "error-429-dialog",
            subType: "error",
            title: "Se han realizado demasiados intentos",
            content: "Por favor, inténtelo de nuevo más tarde."
          });
          break;

        case 422:
          if (!errors || errors.length === 0) return;

          // Check for IDOR security exception
          const hasIdorException = errors.some(
            (item) => item.title === "InsecureDirectObjectReferenceException"
          );
          if (hasIdorException) {
            yield put(appActions.authUserLogout());
            yield call(replace, routesObject.login.url);
          }
          break;

        case 500:
          FullStory.sendEvent("Unhandled Exception", {
            api,
            code,
            title,
            message
          });
          const confirmRes = yield call(showConfirmDialogAndWaitForAction, {
            id: "error-500-dialog",
            subType: "error",
            title: "Aviso",
            content:
              "Ha ocurrido un error. Vuelva a cargar la página o inténtelo de nuevo.",
            showButtonNo: true,
            buttonNo: "Recargar"
          });
          if (confirmRes.no) {
            window.location.reload();
          }
          break;

        case 403:
          FullStory.sendEvent("Forbidden", {
            api,
            code,
            title,
            message
          });
          yield call(showConfirmDialogAndWaitForAction, {
            id: "error-403-dialog",
            subType: "error",
            title: "Acceso denegado",
            content: "No tienes permiso para acceder a este recurso."
          });
          break;

        case 502:
        case 503:
        case 504:
          FullStory.sendEvent("Service Unavailable", {
            api,
            status,
            code,
            title,
            message
          });
          const serviceErrorRes = yield call(showConfirmDialogAndWaitForAction, {
            id: "service-unavailable-dialog",
            subType: "error",
            title: "Servicio no disponible",
            content:
              "El servicio está temporalmente no disponible. Por favor, inténtelo de nuevo en unos minutos.",
            showButtonNo: true,
            buttonNo: "Recargar"
          });
          if (serviceErrorRes.no) {
            window.location.reload();
          }
          break;

        default:
          if (status >= 500) {
            FullStory.sendEvent("Server Error", {
              api,
              status,
              code,
              title,
              message
            });
            const serverErrorRes = yield call(showConfirmDialogAndWaitForAction, {
              id: "server-error-dialog",
              subType: "error",
              title: "Error del servidor",
              content:
                "Ha ocurrido un error en el servidor. Por favor, inténtelo de nuevo más tarde.",
              showButtonNo: true,
              buttonNo: "Recargar"
            });
            if (serverErrorRes.no) {
              window.location.reload();
            }
          }
          else if (status >= 400) {
            FullStory.sendEvent("Client Error", {
              api,
              status,
              code,
              title,
              message
            });
          }
          return;
      }
    }
    finally {
      statusHandlingInFlight.delete(status);
    }
  }
  catch (err) {
    ErrorLoggerService("APP", "errorHandlerSaga:handleResponseErrors", err);
  }
}

function* handleRequestFailed({ payload }: { payload: RequestFailedPayload }) {
  try {
    const { url, method, status, code, message } = payload;

    // Handle network errors, timeouts, and offline scenarios
    const isNetworkError = code === "ERR_NETWORK";
    const isTimeout = code === "ECONNABORTED";
    const isCancelled = code === "ERR_CANCELED";

    // Don't show error for cancelled requests (user intentionally cancelled)
    if (isCancelled) return;

    if (isNetworkError || isTimeout) {
      const eventName = isTimeout ? "Request Timeout" : "Network Error";

      FullStory.sendEvent(eventName, {
        api: url,
        method,
        status,
        code,
        message
      });

      if (!onlineManager.isOnline()) return;

      const description = isTimeout ?
        "Su solicitud ha expirado. Comprueba tu conexión a Internet y vuelve a intentarlo." :
        "Comprueba tu conexión a Internet y vuelve a intentarlo.";

      const confirmResponse = yield call(showConfirmDialogAndWaitForAction, {
        id: "offline-status-dialog",
        subType: "error",
        title: "Aviso",
        content: "¿Estás sin conexión?",
        description,
        showButtonNo: true,
        buttonNo: "Recargar"
      });

      if (confirmResponse.no) {
        window.location.reload();
      }
    }
  }
  catch (err) {
    ErrorLoggerService("APP", "errorHandlerSaga:handleRequestFailed", err);
  }
}

export default function* errorHandlerSaga() {
  const statusHandlingInFlight = new Set<number>();

  yield fork(handleRequestProgressBar);
  yield takeEvery(
    API_CLIENT_ACTIONS.RESPONSE_RECEIVED as any,
    handleResponseErrors,
    statusHandlingInFlight
  );
  yield takeLeading(
    API_CLIENT_ACTIONS.REQUEST_FAILED as any,
    handleRequestFailed
  );
}
